import React from 'react';

/* 화면 오류 경계(1005h · 공명 SA-T441 고쳐서) — 그리기 중 오류·지연 불러오기(덩이) 실패를 받아 빈 화면 대신 대체 화면.
 *  scope='app'     = 맨 위(index.tsx) · 그 밖 모든 오류 · 전체 화면 · [새로고침] 단추 초점
 *  scope='section' = 학생·원장 본문 · 머리·메뉴·AI 서랍은 산다 · resetKey(메뉴)가 바뀌면 풀림 = 다른 메뉴로 이동 가능
 *  ★자동 새로고침 없음 — 저장 안 한 입력(AI 서랍 보내기 전 글 등)을 지우고 · 실패만으로 새 배포라 단정 못 함(SA-T441 🔴1·🔴2).
 *  ★경계 밖: 이벤트 손·비동기 콜백 오류(Firestore 오류 콜백은 그대로 둔다) · 첫 진입 JS 자체가 안 도는 실패.
 *  ★풀어도 실패한 lazy 가져오기는 다시 돌지 않는다 — 「다시 시도」 단추는 두지 않는다(React lazy 는 실패를 기억). */

interface Props { scope: 'app' | 'section'; resetKey?: string; children: React.ReactNode }
interface State { hasError: boolean; key?: string }   // 오류 값의 참·거짓으로 판단 안 함 — throw false·0·'' 도 잡는다(공명 SA-T442 🔴1)

const MSG = '화면을 불러오지 못했습니다.';
const WARN = '새로고침하면 작성 중인 내용이 사라질 수 있습니다.';

export default class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false, key: this.props.resetKey };
    private btn = React.createRef<HTMLButtonElement>();

    static getDerivedStateFromError(): Partial<State> {
        return { hasError: true };
    }

    static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
        if (props.resetKey !== state.key) return { key: props.resetKey, hasError: false };   // 메뉴가 바뀌면 새 화면을 연다
        return null;
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo) {
        console.error(`[오류 경계 ${this.props.scope}]`, error, info.componentStack);
    }

    componentDidUpdate(_: Props, prev: State) {
        if (this.props.scope === 'app' && this.state.hasError && !prev.hasError) this.btn.current?.focus();
    }

    componentDidMount() {
        if (this.props.scope === 'app' && this.state.hasError) this.btn.current?.focus();
    }

    render() {
        if (!this.state.hasError) return this.props.children;
        const reload = () => window.location.reload();
        if (this.props.scope === 'app') {
            return (
                <div role="alert" data-error-boundary="app" className="min-h-screen w-full flex flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
                    <h2 className="text-lg font-black text-slate-800">{MSG}</h2>
                    <p className="text-sm text-slate-500">{WARN}</p>
                    <button ref={this.btn} type="button" onClick={reload} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300">새로고침</button>
                </div>
            );
        }
        return (
            <div role="alert" data-error-boundary="section" className="mx-auto my-10 max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center space-y-3">
                <p className="text-base font-black text-slate-800">{MSG}</p>
                <p className="text-sm text-slate-500">다른 메뉴를 이용하거나 새로고침해 주십시오. {WARN}</p>
                <button type="button" onClick={reload} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300">새로고침</button>
            </div>
        );
    }
}
